import { useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";
import { PageHeader } from "@/components/common";
import { Search, Loader2, X } from "lucide-react";

function scoreClass(s) {
  if (s >= 80) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (s >= 60) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-rose-50 text-rose-700 border-rose-200";
}

const COLS = [
  { key: "name", label: "User" },
  { key: "total", label: "Total" },
  { key: "completed", label: "Completed" },
  { key: "on_time_rate", label: "On-time %" },
  { key: "delayed", label: "Delayed" },
  { key: "pending", label: "Pending" },
  { key: "overdue", label: "Overdue" },
  { key: "score", label: "Score" },
];

export default function UserPerformance() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("score");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    setLoading(true);
    api.get("/dashboard/user-performance")
      .then((r) => setRows(r.data?.data?.rows || []))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let r = rows.filter((x) => (x.name || "").toLowerCase().includes(search.toLowerCase()));
    r = [...r].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      const cmp = typeof av === "string" ? String(av).localeCompare(String(bv)) : (av - bv);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return r;
  }, [rows, search, sortKey, sortDir]);

  const summary = useMemo(() => {
    if (!rows.length) return null;
    const avg = Math.round(rows.reduce((s, r) => s + r.score, 0) / rows.length);
    const overdue = rows.reduce((s, r) => s + r.overdue, 0);
    const pending = rows.reduce((s, r) => s + r.pending, 0);
    return { users: rows.length, avg, overdue, pending };
  }, [rows]);

  const setSort = (k) => {
    if (k === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("desc"); }
  };

  return (
    <div data-testid="user-performance-page">
      <PageHeader title="User Performance" breadcrumbs={["Reports", "User Performance"]} />

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {[["Users with tasks", summary.users], ["Avg score", summary.avg], ["Pending", summary.pending], ["Overdue", summary.overdue]].map(([l, v]) => (
            <div key={l} className="erp-card p-3">
              <div className="text-xs uppercase tracking-wider text-slate-500">{l}</div>
              <div className="text-2xl font-heading mt-1">{v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="erp-card p-3 mb-3 flex items-center gap-2">
        <div className="relative min-w-[220px] max-w-xs flex-1">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search user..."
            className="erp-input pl-8 text-sm" data-testid="perf-search" />
          {search && <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={12} /></button>}
        </div>
      </div>

      <div className="erp-card overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2" /> Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No performance data yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                {COLS.map((c) => (
                  <th key={c.key} className="px-3 py-2 cursor-pointer select-none hover:text-slate-800" onClick={() => setSort(c.key)}>
                    {c.label}{sortKey === c.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.user_id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium">{r.name}{!r.is_active && <span className="ml-1 text-[10px] text-slate-400">(inactive)</span>}</td>
                  <td className="px-3 py-2">{r.total}</td>
                  <td className="px-3 py-2">{r.completed} <span className="text-slate-400">({r.completion_rate}%)</span></td>
                  <td className="px-3 py-2">{r.on_time_rate}%</td>
                  <td className="px-3 py-2">{r.delayed}</td>
                  <td className="px-3 py-2">{r.pending}</td>
                  <td className="px-3 py-2">{r.overdue > 0 ? <span className="text-rose-600 font-medium">{r.overdue}</span> : 0}</td>
                  <td className="px-3 py-2">
                    <span className={`badge-base ${scoreClass(r.score)}`}>{r.score}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
